import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  BookOpen, 
  Calendar, 
  User, 
  Tag, 
  Folder,
  AlertTriangle,
  Info,
  Edit,
  History,
  ExternalLink
} from 'lucide-react';
import api from '../../services/api';
import ReactMarkdown from 'react-markdown';

const KnowledgeBaseArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});

  useEffect(() => {
    fetchUser();
    fetchArticle();
  }, [id]);

  const fetchUser = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
  };

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/kb/articles/${id}`);
      setArticle(response.data.data);
    } catch (error) {
      console.error('Failed to fetch article:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PUBLISHED':
        return 'text-green-600 bg-green-100';
      case 'DRAFT':
        return 'text-yellow-600 bg-yellow-100';
      case 'ARCHIVED':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const canEdit = user.role === 'master_admin';

  const CustomComponents = {
    h1: ({children}) => <h1 className="text-3xl font-bold text-gray-900 mb-6 mt-8 first:mt-0">{children}</h1>,
    h2: ({children}) => <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-6">{children}</h2>,
    h3: ({children}) => <h3 className="text-xl font-bold text-gray-900 mb-3 mt-5">{children}</h3>,
    p: ({children}) => <p className="text-gray-700 mb-4 leading-relaxed">{children}</p>,
    ul: ({children}) => <ul className="list-disc list-inside mb-4 space-y-2">{children}</ul>,
    ol: ({children}) => <ol className="list-decimal list-inside mb-4 space-y-2">{children}</ol>,
    li: ({children}) => <li className="text-gray-700">{children}</li>,
    code: ({inline, children}) => 
      inline ? (
        <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm">{children}</code>
      ) : (
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
          <code>{children}</code>
        </pre>
      ),
    blockquote: ({children}) => (
      <blockquote className="border-l-4 border-indigo-500 pl-4 py-2 mb-4 bg-indigo-50 italic">
        {children}
      </blockquote>
    ),
    a: ({href, children}) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline flex items-center gap-1 inline">
        {children}
        <ExternalLink className="w-3 h-3" />
      </a>
    ),
    table: ({children}) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border border-gray-200 rounded-lg">
          {children}
        </table>
      </div>
    ),
    th: ({children}) => (
      <th className="bg-gray-50 px-4 py-2 text-left text-sm font-medium text-gray-900 border-b">
        {children}
      </th>
    ),
    td: ({children}) => (
      <td className="px-4 py-2 text-sm text-gray-700 border-b">
        {children}
      </td>
    ),
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="p-6">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Article not found</h3>
          <p className="text-gray-500">The article you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Knowledge Base
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{article.title}</h1>
            <p className="text-xl text-gray-600 mb-4">{article.summary}</p>
            
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Folder className="w-4 h-4" />
                {article.category_name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Updated {new Date(article.updated_at).toLocaleDateString()}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(article.status)}`}>
                {article.status}
              </span>
            </div>

            {article.tags && article.tags.length > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <Tag className="w-4 h-4 text-gray-400" />
                <div className="flex gap-2">
                  {article.tags.map(tag => (
                    <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {canEdit && (
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => navigate(`/admin/knowledge-base/edit/${article.id}`)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        {article.change_log && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <History className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Latest Changes</h4>
                <p className="text-blue-700 text-sm mt-1">{article.change_log}</p>
              </div>
            </div>
          </div>
        )}

        <div className="prose prose-lg max-w-none">
          <ReactMarkdown components={CustomComponents}>
            {article.content || 'No content available'}
          </ReactMarkdown>
        </div>
      </div>

      {/* Event Mappings */}
      <div className="mt-6 bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Related Events & Errors
        </h3>
        <p className="text-gray-600 mb-4">
          This article helps with the following system events:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* This would be populated from event mappings */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <code className="text-sm font-mono text-gray-800">AUTH_FAILED</code>
            <p className="text-sm text-gray-600 mt-1">Authentication errors</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <code className="text-sm font-mono text-gray-800">SMS_DELIVERY_FAILED</code>
            <p className="text-sm text-gray-600 mt-1">SMS delivery failures</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseArticle;
